import CryptoJS from "crypto-js";
import moment from "moment";
import {
  getAuditees,
  getAuditModes,
  getAuditTypes,
  getFeedbackList,
  getRolesList,
  getTeamlead,
  getTeamMembers,
  getTeams,
  getVendorList,
} from "./api/common";
import {
  AuditOption,
  FeedbackData,
  JobRoleData,
  SelectOption,
  VendorData,
} from "./commonTypes";
import {
  AUDIT_LEVEL,
  AUDIT_MODES,
  MAXIMUM_RESULT_PER_PAGE,
  MONTHS,
} from "./constants";
import Alert from "./Components/AlertPopup";
import { env } from "../env";

/**
 *
 * @param date
 * @param format
 * @returns
 */
export const getDateFormat = (date: Date, format: string): string => {
  return moment(date).format(format);
};

export const getDateFormatString = (date: string, format: string): string => {
  return moment(date).format(format);
};
/**
 * Store a value in sessionStorage
 * @param key
 * @param value
 */
export const setSessionStorage = (key: string, value: string | number) => {
  sessionStorage.setItem(key, JSON.stringify({ value }));
};

/**
 * Get a value from sessionStorage
 * @param key
 * @returns
 */
export const getSessionStorage = (key: string) => {
  const itemStr = sessionStorage.getItem(key);

  if (!itemStr) {
    return null;
  }

  try {
    const item = JSON.parse(itemStr);
    return item.value;
  } catch (e) {
    return null;
  }
};

export const getJobRoleSelectOptions = async () => {
  const response = await getRolesList();
  const finalSet: SelectOption[] = [{ value: "", label: "--Select--" }];
  if (response && response.length > 0) {
    response.forEach((element: JobRoleData) => {
      finalSet.push({ value: element.roleId, label: element.roleName });
    });
  }

  return finalSet;
};

export const getAuditTypesSelectOptions = async () => {
  const response = await getAuditTypes();
  const finalSet: SelectOption[] = [];
  if (response && response.length > 0) {
    response.forEach((element: any) => {
      finalSet.push({
        value: element.auditTypeId,
        label: element.auditTypeName,
      });
    });
  }

  return finalSet;
};

export const getTeamMembersSelectOptions = async () => {
  const response = await getTeamMembers();
  const finalSet: SelectOption[] = [];
  if (response && response.length > 0) {
    response.forEach((element: any) => {
      finalSet.push({
        value: element.userId,
        label: element.name,
      });
    });
  }

  return finalSet;
};
export const getAuditeeSelectOptions = async () => {
  const response = await getTeamMembers();
  const finalSet: AuditOption[] = [];
  if (response && response.length > 0) {
    response.forEach((element: any) => {
      finalSet.push({
        value: element.userId,
        label: element.name,
        email: element.email,
      });
    });
  }

  return finalSet;
};

export const getTeamsSelectOptions = async () => {
  const response = await getTeams();
  const finalSet: SelectOption[] = [{ value: "", label: "--Select--" }];
  if (response && response.length > 0) {
    response.forEach((element: any) => {
      finalSet.push({ value: element.teamId, label: element.teamName });
    });
  }

  return finalSet;
};

export const getAuditModesSelectOptions = async () => {
  const response = await getAuditModes();
  const finalSet: SelectOption[] = [];
  if (response && response.length > 0) {
    response.forEach((element: any) => {
      finalSet.push({ value: element.value, label: element.label });
    });
  }

  return finalSet;
};

export const getAuditeesSelectOptions = async () => {
  const response = await getAuditees();
  const finalSet: SelectOption[] = [{ value: "", label: "--Select--" }];
  if (response && response.length > 0) {
    response.forEach((element: any) => {
      finalSet.push({ value: element.auditeeId, label: element.auditeeName });
    });
  }

  return finalSet;
};
export const getTeamleadSelectOptions = async () => {
  const response = await getTeamlead();
  const finalSet: SelectOption[] = [{ value: "", label: "--Select--" }];
  if (response && response.length > 0) {
    response.forEach((element: any) => {
      finalSet.push({ value: element.name, label: element.name });
    });
  }

  return finalSet;
};

export const getFeedbackOptions = async () => {
  const response = await getFeedbackList();
  const finalSet: SelectOption[] = [{ value: "", label: "--Select--" }];
  if (response && response.length > 0) {
    response.forEach((element: FeedbackData) => {
      finalSet.push({
        value: element.feedbackTypeId,
        label: element.feedbackName,
      });
    });
  }

  return finalSet;
};

/**
 * to encrypt string based on crypto
 * @param text
 * @returns
 */
export const encrypt = (text: string | number) => {
  const secret = env.VITE_ENCRYPTION_SECRET
    ? env.VITE_ENCRYPTION_SECRET
    : "SOME_SECRET";
  const iv = env.VITE_ENCRYPTION_IV
    ? env.VITE_ENCRYPTION_IV
    : "SOME_IV";

  const ciphertext = CryptoJS.AES.encrypt(
    text.toString(),
    CryptoJS.enc.Utf8.parse(secret),
    {
      iv: CryptoJS.enc.Utf8.parse(iv),
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    },
  ).toString();
  return btoa(ciphertext);
};

/**
 * to decrypt a encoded string using crypto
 * @param encodedText
 * @returns
 */
export const decrypt = (encodedText: string) => {
  const secret = env.VITE_ENCRYPTION_SECRET
    ? env.VITE_ENCRYPTION_SECRET
    : "SOME_SECRET";
  const iv = env.VITE_ENCRYPTION_IV
    ? env.VITE_ENCRYPTION_IV
    : "SOME_IV";

  const bytes = CryptoJS.AES.decrypt(
    atob(encodedText),
    CryptoJS.enc.Utf8.parse(secret),
    {
      iv: CryptoJS.enc.Utf8.parse(iv),
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    },
  );
  return bytes.toString(CryptoJS.enc.Utf8);
};

export const getVendorListOptions = async () => {
  const response = await getVendorList();
  const finalSet: SelectOption[] = [{ value: "", label: "--Select--" }];
  if (response && response.length > 0) {
    response.forEach((element: VendorData) => {
      finalSet.push({
        value: element.vendorId,
        label: element.vendorName,
      });
    });
  }

  return finalSet;
};

export const getMonthOptions = () => {
  const finalSet: SelectOption[] = MONTHS.map((label, key) => {
    return { value: key, label };
  });
  return finalSet;
};

export const getYearsOptions = () => {
  var current = new Date().getFullYear();
  var min = current - 9;
  var max = current + 9;
  var years = [];

  for (var i = current; i >= min; i--) {
    const yearValue = i.toString();
    years.push({ value: yearValue, label: yearValue });
  }
  for (var j = current; j <= max; j++) {
    const yearValue = j.toString();
    years.push({ value: yearValue, label: yearValue });
  }
  return years;
};

export const calculateOffset = (
  pageNumber: number,
  totalCount: number = 100, // any point in time, maximum 100 results will be shown in the table - revisit
) => {
  if (pageNumber < 1) {
    throw new Error("Page number must be greater than or equal to 1.");
  }

  const offset = (pageNumber - 1) * MAXIMUM_RESULT_PER_PAGE;
  return Math.max(0, Math.min(offset, totalCount - MAXIMUM_RESULT_PER_PAGE));
};

export const toTitleCase = (str: string): string => {
  if (!str) {
    return "";
  }
  return str.toLowerCase().replace(/\b\w/g, (s) => s.toUpperCase());
};

export const getAuditModeLabel = (
  id: number,
  auditMode: SelectOption[],
): string => {
  return (AUDIT_MODES[id] = AUDIT_MODES[id] || "Unknown Mode");
};

export const getAuditLevelLabel = (id: number): string => {
  return (AUDIT_LEVEL[id] = AUDIT_LEVEL[id] || "Unknown");
};

export const getAuditLevelId = (label: string): number | null => {
  const entry = Object.entries(AUDIT_LEVEL).find(
    ([, value]) => (value as any).toLowerCase() === label.toLowerCase(),
  );
  return entry ? Number(entry[0]) : null;
};

export const parseDate = (dateStr: any) => {
  const [day, month, year] = dateStr.split("-");
  return new Date(`${year}-${month}-${day}`);
};

export const getTodayISODate = () => {
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, "0"); // Months are zero-based
  const dd = String(today.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

export const getDaysDiff = (start: Date, end: Date): number => {
  return moment(end).diff(moment(start), "days") + 1; // +1 to include both start & end
};

export const getUniqueAppNames = (orgs: any[]) => {
  const names: string[] = [];
  orgs.forEach((o) =>
    o.applications?.forEach((a: any) => {
      if (!names.includes(a.applicationName)) names.push(a.applicationName);
    }),
  );
  return names.join(", ");
};
export const blobToImageElement = (blob: Blob): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(blob);

    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };

    img.onerror = reject;
    img.src = url;
  });
};

/**
 * Load organization logos and convert them to URL format
 * @param organizations - Array of organizations with logo data
 * @returns Map of organization IDs to logo URLs
 */
export const loadOrganizationLogos = async <
  T extends { organizationId: number; organizationLogo?: any },
>(
  organizations: T[],
): Promise<Map<number, string>> => {
  const urls = new Map<number, string>();

  for (const org of organizations) {
    if (org.organizationLogo) {
      // Check if it's a Blob or File object
      if (
        org.organizationLogo instanceof Blob ||
        org.organizationLogo instanceof File
      ) {
        try {
          const img = await blobToImageElement(org.organizationLogo);
          urls.set(org.organizationId, img.src);
        } catch (error) {
          console.error(
            "Error loading logo for organization:",
            org.organizationId,
            error,
          );
        }
      }
      // If it's a string
      else if (typeof org.organizationLogo === "string") {
        // Check if it's already a data URL or regular URL
        if (
          org.organizationLogo.startsWith("data:") ||
          org.organizationLogo.startsWith("http")
        ) {
          urls.set(org.organizationId, org.organizationLogo);
        } else {
          // It's a base64 string without the data URL prefix, add it
          const dataUrl = `data:image/jpeg;base64,${org.organizationLogo}`;
          urls.set(org.organizationId, dataUrl);
        }
      }
    }
  }

  return urls;
};

export const formatMinutes = (mins: number): string => {
  if (!mins || mins <= 0) return "0h";

  const days = Math.floor(mins / 1440); // 1440 = minutes in a day
  const hours = Math.floor((mins % 1440) / 60);
  const minutes = mins % 60;

  let result = [];

  if (days > 0) result.push(`${days}d`);
  if (hours > 0) result.push(`${hours}h`);
  if (minutes > 0) result.push(`${minutes}m`);

  return result.join(" ");
};

interface SequenceItem {
  id?: string;
  sequenceId?: string | number;
  name?: string;
  sequenceName?: string;
  sequenceOrderId?: number;
  sequenceStatus?: boolean;
  [key: string]: any;
}

export function DraggableItem({
  step,
  index,
  onDragStart,
  onDrop,
}: {
  step: SequenceItem;
  index: number;
  onDragStart: (index: number) => void;
  onDrop: (index: number) => void;
}) {
  return (
    <div
      draggable
      onDragStart={() => onDragStart(index)}
      onDragOver={(e) => e.preventDefault()} // allow drop
      onDrop={() => onDrop(index)}
      className="draggable-item"
    >
      <span className="draggable-label">{step.name || step.sequenceName}</span>

      <span className="draggable-step-number">Step {index + 1}</span>
    </div>
  );
}

export const resolveLogoUrl = (logo: any): string | null => {
  if (!logo) return null;

  if (logo instanceof Blob || logo instanceof File) {
    return URL.createObjectURL(logo);
  }

  if (typeof logo === "string") {
    if (logo.startsWith("data:") || logo.startsWith("http")) {
      return logo;
    }
    return `data:image/jpeg;base64,${logo}`;
  }

  return null;
};
